import type { CollectionModel } from '@ikegps/core-shared';
import { addStyles } from '../../components/theme';
import { T, Translate } from '../../i18n';
import { m } from '../../mithril';
import type { TopologicalEditorController } from '../../page/topological.editor.page';
import { classNames } from '../../util/classnames';
import { CSS, Style } from './topological.editor.collection.list.style';

addStyles(Style);

interface ITopologicalEditorCollectionListOptions {
	onItemClick: (id: string) => void;
	selectedRevision?: string | null;
	collections: CollectionModel[];
	collectionsEditController: TopologicalEditorController;
}

export const TopologicalEditorCollectionList = {
	view(options: ITopologicalEditorCollectionListOptions) {
		const collections = options.collections.filter((collection): collection is CollectionModel => collection != null);

		// Group variants under their parent pole (by uuid) so they render nested rather than as flat
		// siblings — a variant is a child of the pole above it, and only selectable from there.
		const variantsByParentUuid = new Map<string, CollectionModel[]>();
		for (const collection of collections) {
			if (collection.variantForUuid != null) {
				const variants = variantsByParentUuid.get(collection.variantForUuid) ?? [];
				variants.push(collection);
				variantsByParentUuid.set(collection.variantForUuid, variants);
			}
		}
		const parents = collections.filter((collection) => collection.variantForUuid == null);

		return m(
			'div',
			{ className: CSS.container },
			parents.flatMap((parent) => [
				TopologicalEditorCollectionList.viewItem(options, parent, false),
				...(variantsByParentUuid.get(parent.uuid) ?? []).map((variant) =>
					TopologicalEditorCollectionList.viewItem(options, variant, true)
				),
			])
		);
	},

	viewItem(options: ITopologicalEditorCollectionListOptions, pole: CollectionModel, isVariant: boolean) {
		const hasChanges = options.collectionsEditController.collectionHasChanges(pole.revision);
		// A parent shows its pole title; a variant shows its type name (e.g. "Proposed") since it shares
		// the parent's title.
		const label = isVariant
			? (pole.variantTypeName ?? pole.variantType ?? Translate(T.COLLECTION_MISSING_TITLE))
			: pole.title
				? pole.title
				: Translate(T.COLLECTION_MISSING_TITLE);

		return m(
			'div',
			{
				className: classNames(CSS.item, {
					[CSS.variant]: isVariant,
					[CSS.selected]: options.selectedRevision === pole.revision,
					[CSS.noLocation]: !pole.location,
				}),
				role: 'button',
				tabindex: '0',
				onclick: () => options.onItemClick(pole.revision),
			},
			[
				m('div', { className: classNames({ [CSS.unsaved]: hasChanges }) }, [
					label,
					pole.isMissingTrueSize() ? m('span', { className: CSS.redDot }) : null,
				]),
				m('div', { className: CSS.info }, hasChanges ? `(${Translate(T.TOPO_UNSAVED_COLLECTION)})` : ''),
			]
		);
	},
};
